<template>
	<v-app>
		<v-overlay :model-value="!subscriptionReady" class="align-center justify-center" persistent>
			<div class="text-center">
				<p>Please wait while we retrieve your account information.</p>
				<v-progress-circular indeterminate color="primary"> </v-progress-circular>
			</div>
		</v-overlay>
		<main class="main d-flex align-center">
			<v-row class="justify-center">
				<div class="d-flex text-center justify-center w-100">
					<img src="https://assets.liveswitch.com/shared/liveswitch-logotype.svg" alt="LiveSwitch" height="72" />
				</div>
				<v-col cols="8" md="4">
					<div v-if="subscriptionReady && accountSetupError" class="text-center" role="alert">
						<h6 class="mt-4 mb-2">We couldn't load your account details.</h6>
						<p class="mb-4">Please try again. If this keeps happening, reopen the account setup link from your email.</p>
						<v-btn data-test-id="retry-account-setup" color="primary" variant="flat" rounded="xl" @click="retryAccountSetup"
							>Try Again</v-btn
						>
					</div>
					<div v-else-if="subscriptionReady">
						<h6 class="text-center mt-2 mb-2">Set your password.</h6>

						<v-form id="setup-form" ref="form">
							<v-text-field
								v-model="email"
								type="email"
								variant="outlined"
								hide-details="auto"
								readonly
								disabled
								class="field-border-radius"
								placeholder="Email Address"></v-text-field>

							<v-text-field
								v-model="password"
								type="text"
								variant="outlined"
								hide-details="auto"
								:rules="passwordRules"
								class="field-border-radius"
								autocomplete="new-password"
								placeholder="Password"></v-text-field>

							<v-text-field
								v-model="confirmPassword"
								type="text"
								variant="outlined"
								hide-details="auto"
								:rules="confirmPasswordRules"
								class="field-border-radius"
								autocomplete="new-password"
								placeholder="Confirm Password"></v-text-field>

							<div v-if="!accountSetupComplete">
								<v-btn id="submit-button" class="w-100" color="primary" :loading="loading" @click="completeAccountSetup"
									>Submit</v-btn
								>

								<div v-show="false">
									<p v-if="subscriptionInfo?.oAuthProviders" class="text-center mt-4 mb-4">Or</p>

									<v-btn v-for="provider in subscriptionInfo?.oAuthProviders" class="btn-sso">
										<div class="d-flex align-center mr-2">
											<img :src="`/assets/sso/${provider.name}.svg`" :alt="provider.name" />
										</div>
										<div class="name">Sign in with {{ provider.name }}</div>
									</v-btn>
								</div>
							</div>
						</v-form>
					</div>
				</v-col>
			</v-row>
			<v-snackbar id="snackbar" v-model="showSnackbar" top :timeout="snackbarTimeout">
				{{ message }}
			</v-snackbar>
		</main>
	</v-app>
</template>

<script lang="ts" setup>
	import { onMounted, ref, computed, getCurrentInstance, watch } from "vue";
	import { setupAccount, getSubscriptionInfo } from "./api/payments";
	import { SubscriptionInfo } from "./types/payments/SubscriptionInfo";
	import { AccountSetupRequest } from "./types/payments/AccountSetupRequest";
	import { AccountSetupResponse } from "./types/payments/AccountSetupResponse";
	import { supportHint } from "./helpers/userMessages";

	const showSnackbar = ref<boolean>(false);
	const snackbarTimeout = ref<number>(5000);
	const instance = ref();
	const message = ref<string>("");
	const email = ref<string>("");
	const password = ref<string>("");
	const confirmPassword = ref<string>("");
	const params = ref<URLSearchParams>();
	const form = ref<any>();
	const subscriptionMaxChecks = 4;
	const subscriptionCheckDelayMs = 2500;
	const subscriptionChecks = ref<number>(0);
	const subscriptionInfo = ref<SubscriptionInfo>();
	const subscriptionReady = ref<boolean>(false);
	const accountSetupError = ref<boolean>(false);
	const accountSetupComplete = ref<boolean>();
	const loading = ref<boolean>();

	const valid = computed(() => {
		return Boolean(email.value && password.value && confirmPassword.value && password.value === confirmPassword.value);
	});

	watch(password, (newPassword, oldPassword) => {
		if (confirmPassword.value) {
			void form.value?.validate();
		}
	});

	watch(confirmPassword, (newPassword, oldPassword) => {
		void form.value?.validate();
	});

	const passwordRules = ref<Array<any>>([
		(v) => !!v || "Password is required.",
		(v) =>
			/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z\d\S]{8,}$/.test(v) ||
			"The password must be at least 8 characters long and include 1 uppercase character, 1 lowercase character, 1 number, and may contain special characters.",
		(v) => v.length < 50 || "Password must be less than 50 characters long."
	]);

	const confirmPasswordRules = ref<Array<any>>([(v) => v == password.value || "The passwords must match."]);

	onMounted(async () => {
		instance.value = getCurrentInstance();
		params.value = new URLSearchParams(window.location.search);
		await getCheckoutDetails();
	});

	async function getCheckoutDetails(): Promise<void> {
		subscriptionReady.value = false;
		accountSetupError.value = false;
		subscriptionInfo.value = undefined;
		email.value = "";

		const providerQueryValue = params.value?.get("paymentProvider") ?? params.value?.get("provider");
		const provider: string = providerQueryValue?.trim() || "Stripe";
		const sessionId: string = params.value?.get("checkoutSessionId")?.trim() ?? "";

		if (!sessionId) {
			showAccountSetupError("Account details lookup blocked because checkoutSessionId is missing.", provider, sessionId);
			return;
		}

		const subscription: SubscriptionInfo | null = await getSubscriptionInfo(provider, sessionId);

		if (!subscription || subscription.error) {
			if (scheduleAccountSetupRetry("Account details lookup failed.", provider, sessionId)) {
				return;
			}

			showAccountSetupError("Account details lookup failed after all retry attempts.", provider, sessionId);
			return;
		}

		// If the user already completed their account setup, redirect them instead of prompting to set up again.
		// This is the scenario where they set up their account and click on the setup link in the welcome email.
		if (subscription.state === "Complete") {
			if (subscription.redirectUrl) {
				window.location.href = subscription.redirectUrl;
				return;
			}

			showAccountSetupError("Completed account setup response is missing its redirect URL.", provider, sessionId);
			return;
		}

		if (subscription.state === "New") {
			if (scheduleAccountSetupRetry("Account setup is still pending.", provider, sessionId)) {
				return;
			}

			showAccountSetupError("Account setup remained pending after all retry attempts.", provider, sessionId);
			return;
		}

		if (subscription.state !== "Pending") {
			showAccountSetupError(`Account setup returned terminal state "${subscription.state}".`, provider, sessionId);
			return;
		}

		const subscriptionEmail = typeof subscription.email === "string" ? subscription.email.trim() : "";
		const subscriptionTransactionId =
			typeof subscription.subscriptionTransactionId === "string" ? subscription.subscriptionTransactionId.trim() : "";

		if (!subscriptionEmail || !subscriptionTransactionId) {
			if (scheduleAccountSetupRetry("Account setup response is missing required form data.", provider, sessionId)) {
				return;
			}

			showAccountSetupError("Account setup response remained incomplete after all retry attempts.", provider, sessionId);
			return;
		}

		subscriptionInfo.value = {
			...subscription,
			email: subscriptionEmail,
			subscriptionTransactionId
		};
		email.value = subscriptionEmail;
		subscriptionReady.value = true;
	}

	function scheduleAccountSetupRetry(reason: string, provider: string, sessionId: string): boolean {
		if (subscriptionChecks.value >= subscriptionMaxChecks) {
			return false;
		}

		subscriptionChecks.value++;
		console.warn(`[AccountSetup] ${reason} Retrying account details lookup.`, {
			provider,
			sessionIdPresent: Boolean(sessionId),
			retryAttempt: subscriptionChecks.value,
			maximumRetryAttempts: subscriptionMaxChecks
		});
		setTimeout(() => void getCheckoutDetails(), subscriptionCheckDelayMs);
		return true;
	}

	function showAccountSetupError(reason: string, provider: string, sessionId: string): void {
		console.warn(`[AccountSetup] ${reason} Showing recovery guidance.`, {
			provider,
			sessionIdPresent: Boolean(sessionId),
			retryAttempts: subscriptionChecks.value
		});
		accountSetupError.value = true;
		subscriptionReady.value = true;
	}

	function retryAccountSetup(): void {
		subscriptionChecks.value = 0;
		void getCheckoutDetails();
	}

	async function completeAccountSetup(): Promise<void> {
		const result = await form.value?.validate();
		if (!result?.valid) {
			return;
		}

		loading.value = true;
		const request: AccountSetupRequest = {
			subscriptionTransactionId: subscriptionInfo.value?.subscriptionTransactionId,
			password: password.value,
			confirmPassword: password.value
		};

		const response: AccountSetupResponse = await setupAccount(request);

		if (!response.error) {
			accountSetupComplete.value = true;
			message.value = "Your account has been set up successfully. You will be redirected shortly.";
			showSnackbar.value = true;
			setTimeout(() => redirectAfterAccountSetup(response), snackbarTimeout.value);
		} else {
			console.warn("[AccountSetup] Account setup completion failed. Showing the response message.", {
				subscriptionTransactionIdPresent: Boolean(request.subscriptionTransactionId)
			});
			message.value = response.message;
			showSnackbar.value = true;
		}

		loading.value = false;
	}

	// LSP-21508: gate AND navigate on the completion response's redirectUrl. The page-load lookup is
	// always "Pending" when this form is shown and usually has no redirectUrl, so gating on it left the
	// customer stuck on "You will be redirected shortly". There is deliberately no fallback to the lookup's
	// URL: it predates account creation, and the completion response is the source of the post-setup URL.
	function redirectAfterAccountSetup(response: AccountSetupResponse): void {
		const redirectUrl = typeof response.redirectUrl === "string" ? response.redirectUrl.trim() : "";
		const logContext = {
			subscriptionTransactionIdPresent: Boolean(response.subscriptionTransactionId),
			lookupState: subscriptionInfo.value?.state,
			redirectUrlType: typeof response.redirectUrl
		};

		if (!redirectUrl) {
			skipRedirect("account setup response has no redirect URL.", logContext);
			return;
		}

		let redirectProtocol: string;
		try {
			redirectProtocol = new URL(redirectUrl, window.location.href).protocol;
		} catch {
			skipRedirect("account setup response redirect URL is not a valid URL.", logContext);
			return;
		}

		// Only follow web URLs: a javascript:/data: value would run in this page instead of navigating.
		if (redirectProtocol !== "https:" && redirectProtocol !== "http:") {
			skipRedirect("account setup response redirect URL uses an unsupported protocol.", { ...logContext, redirectProtocol });
			return;
		}

		window.location.href = redirectUrl;
	}

	// The account now exists, so resubmitting is not the answer — tell the customer to sign in instead of
	// leaving the "You will be redirected shortly" promise on screen with nothing happening.
	function skipRedirect(reason: string, logContext: Record<string, unknown>): void {
		console.warn(`[AccountSetup] Redirect skipped: ${reason}`, logContext);
		snackbarTimeout.value = -1;
		message.value = `Your account is set up, but we couldn't sign you in automatically. Please sign in to continue. ${supportHint()}`;
		showSnackbar.value = true;
	}
</script>

<style>
	input::-ms-input-placeholder {
		color: #606060 !important;
	}

	input::-webkit-input-placeholder {
		color: #606060 !important;
	}

	input::placeholder {
		color: #606060 !important;
	}
</style>

<style lang="scss" scoped>
	#app {
		display: block !important;
	}

	nav .v-icon {
		padding-right: 8px;
		font-size: 18px;
	}

	.action {
		cursor: pointer;
	}

	.main {
		height: 100vh;
	}

	.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
		background-color: #0070ff !important;
		opacity: 0.24;
	}
	.theme--light.v-btn.v-btn--disabled {
		color: white !important;
	}
	.theme--light.v-btn.v-btn--disabled .v-icon,
	.theme--light.v-btn.v-btn--disabled .v-btn__loading {
		color: white !important;
	}

	.v-btn {
		width: 100%;
		height: 48px !important;
		border-radius: 12px;
	}

	.btn-sso {
		background-color: #fff !important;
	}
</style>
